// 選択中オブジェクトに対して 8 方位リサイズハンドルと 1 つの回転ハンドルを描画する。
// ハンドルはオブジェクトと同じ回転を持つ選択ボックス内に配置し、ドラッグ中の DOM
// プレビューにも追従するよう各入力ハンドラから選択ボックスを直接更新する。

import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { useResizeHandle, type ResizeDirection } from "./input/use-resize-handle";
import { useRotationHandle } from "./input/use-rotation-handle";
import { keepsAspectRatio } from "../../lib/object-aspect";

const HANDLE_SIZE = 10;
const ROTATION_HANDLE_OFFSET = 28;

export function CanvasSelectionOverlay(): JSX.Element | null {
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);
  const tool = useCanvasStore((s) => s.tool);
  const objects = useCanvasDocStore((s) => s.objects);

  // 配置ツール（付箋 / 図形 / テキスト / スタンプ）の選択中は、リサイズ / 回転ハンドルを
  // 表示しない。配置中は新規オブジェクトの作成が目的であり、選択中オブジェクトのハンドル上から
  // ドラッグを開始したときに、配置ではなくリサイズ / 回転が始まってしまう競合を防ぐ
  // （ハンドルの pointerdown は stopPropagation するため、ステージ側の配置開始へ届かない）。
  if (
    tool === "place-sticky" ||
    tool === "place-shape" ||
    tool === "place-text" ||
    tool === "place-stamp"
  ) {
    return null;
  }

  if (selectedIds.length !== 1) return null;
  const id = selectedIds[0]!;
  const target = objects[id];
  if (!target) return null;
  // ペンストロークは現状、選択後の移動のみ対応し、リサイズ / 回転ハンドルは表示しない。
  if (target.type === "pen-stroke") return null;
  // コネクタ（接続矢印）は端点の図形に追従するため、リサイズ / 回転 / 接続ハンドルは表示しない。
  if (target.type === "connector") return null;
  // ロック中もリサイズ・回転ハンドルを表示しない（移動・編集も他のフックで抑止する）。
  if (target.locked) return null;

  return <SingleSelectionOverlay key={id} object={target} />;
}

interface SingleProps {
  readonly object: Exclude<CanvasObject, { type: "pen-stroke" }>;
}

function SingleSelectionOverlay({ object }: SingleProps): JSX.Element {
  const dispatcher = useCanvasOpDispatcherContext();
  const targetRef = useRef<HTMLElement | null>(null);
  const [, forceTick] = useState(0);

  useLayoutEffect(() => {
    const el = document.querySelector<HTMLElement>(`[data-testid="canvas-object-${object.id}"]`);
    if (targetRef.current !== el) {
      targetRef.current = el ?? null;
      forceTick((n) => n + 1);
    }
  });

  const handleRefs: Record<ResizeDirection, RefObject<HTMLDivElement | null>> = {
    nw: useRef<HTMLDivElement | null>(null),
    n: useRef<HTMLDivElement | null>(null),
    ne: useRef<HTMLDivElement | null>(null),
    e: useRef<HTMLDivElement | null>(null),
    se: useRef<HTMLDivElement | null>(null),
    s: useRef<HTMLDivElement | null>(null),
    sw: useRef<HTMLDivElement | null>(null),
    w: useRef<HTMLDivElement | null>(null),
  };
  const rotationHandleRef = useRef<HTMLDivElement | null>(null);

  // 画像・動画・スタンプはアスペクト比固定。四隅ハンドルのみ表示し、四隅ドラッグでは
  // Shift 無しでも常に縦横比を維持する（辺ハンドルは描画しない）。
  const lockAspect = keepsAspectRatio(object);

  const common = {
    objectId: object.id,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation ?? 0,
    lockAspect,
    targetRef,
  } as const;
  useResizeHandle({ ...common, direction: "nw", handleRef: handleRefs.nw });
  useResizeHandle({ ...common, direction: "n", handleRef: handleRefs.n });
  useResizeHandle({ ...common, direction: "ne", handleRef: handleRefs.ne });
  useResizeHandle({ ...common, direction: "e", handleRef: handleRefs.e });
  useResizeHandle({ ...common, direction: "se", handleRef: handleRefs.se });
  useResizeHandle({ ...common, direction: "s", handleRef: handleRefs.s });
  useResizeHandle({ ...common, direction: "sw", handleRef: handleRefs.sw });
  useResizeHandle({ ...common, direction: "w", handleRef: handleRefs.w });
  useRotationHandle({
    handleRef: rotationHandleRef,
    objectId: object.id,
    dispatcher,
    centerX: object.x + object.w / 2,
    centerY: object.y + object.h / 2,
    initialRotation: object.rotation ?? 0,
    targetRef,
  });

  const allHandlePositions: {
    dir: ResizeDirection;
    left: string;
    top: string;
    cursor: string;
  }[] = [
    { dir: "nw", left: "0%", top: "0%", cursor: "nwse-resize" },
    { dir: "n", left: "50%", top: "0%", cursor: "ns-resize" },
    { dir: "ne", left: "100%", top: "0%", cursor: "nesw-resize" },
    { dir: "e", left: "100%", top: "50%", cursor: "ew-resize" },
    { dir: "se", left: "100%", top: "100%", cursor: "nwse-resize" },
    { dir: "s", left: "50%", top: "100%", cursor: "ns-resize" },
    { dir: "sw", left: "0%", top: "100%", cursor: "nesw-resize" },
    { dir: "w", left: "0%", top: "50%", cursor: "ew-resize" },
  ];
  // アスペクト比固定のオブジェクトは四隅（斜め）ハンドルのみ表示する。
  // 辺ハンドルを描画しないことで、その方位の useResizeHandle は handleRef が null となり何もしない。
  const CORNER_DIRECTIONS: ReadonlySet<ResizeDirection> = new Set(["nw", "ne", "se", "sw"]);
  const handlePositions = lockAspect
    ? allHandlePositions.filter((p) => CORNER_DIRECTIONS.has(p.dir))
    : allHandlePositions;

  return (
    <div
      data-testid="canvas-selection-overlay"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    >
      <div
        data-testid="canvas-selection-box"
        style={{
          position: "absolute",
          left: `${object.x}px`,
          top: `${object.y}px`,
          width: `${object.w}px`,
          height: `${object.h}px`,
          transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
          transformOrigin: "center",
          pointerEvents: "none",
        }}
      >
        {handlePositions.map((p) => (
          <div
            key={p.dir}
            ref={handleRefs[p.dir]}
            data-testid={`selection-handle-${p.dir}`}
            style={{
              position: "absolute",
              left: p.left,
              top: p.top,
              width: `${HANDLE_SIZE}px`,
              height: `${HANDLE_SIZE}px`,
              marginLeft: `${-HANDLE_SIZE / 2}px`,
              marginTop: `${-HANDLE_SIZE / 2}px`,
              background: "var(--color-surface)",
              border: "2px solid var(--color-accent)",
              borderRadius: "2px",
              cursor: p.cursor,
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
          />
        ))}
        <div
          ref={rotationHandleRef}
          data-testid="selection-handle-rotation"
          style={{
            position: "absolute",
            // 上辺中央から、右上の頂点の外側（斜め方向）へ移動する。
            // 接続矢印のドットは 4 辺の中点外側に出るため、回転ハンドルが上辺中央のドットと
            // 重なって矢印操作の邪魔にならないよう、頂点の斜め外へ逃がす（矢印は斜め辺から出さない）。
            left: "100%",
            top: "0%",
            marginLeft: `${ROTATION_HANDLE_OFFSET - HANDLE_SIZE / 2}px`,
            marginTop: `${-ROTATION_HANDLE_OFFSET - HANDLE_SIZE / 2}px`,
            width: `${HANDLE_SIZE}px`,
            height: `${HANDLE_SIZE}px`,
            background: "var(--color-accent)",
            border: "2px solid var(--color-surface)",
            borderRadius: "50%",
            cursor: "grab",
            pointerEvents: "auto",
            boxSizing: "border-box",
          }}
          title="ドラッグで回転（Shift で 15° スナップ）"
        />
      </div>
    </div>
  );
}
